import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BankDetailsCommon from './BankDetailsCommon';
import { useBankDetailsForm } from './useBankDetailsForm';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountType, RoutePaths } from 'Constants/Common';
import { useAuth0 } from '@auth0/auth0-react';
import { PaymentApi } from 'api/PaymentApi';
import { BankDetailType } from '../CommonTypes';

const RepaymentSetupPage = () => {
    const { t } = useTranslation();
    const [, setValidated] = useState(false);
    const [showBankInputSection, setShowBankInputSection] = useState(false);
    const [bankDetailsState, setBankDetailsState] = useState({
        accountHolderName: '',
        sortCode: [
            { key: '', value: '' },
            { key: '', value: '' },
            { key: '', value: '' }
        ], // array of 3 parts
        accountNumber: '',
        paymentDay: '',
        businessPartnerId: 0
    });
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const location = useLocation();

    const policyReferenceNumber =
        location.state ?? ({} as { referenceNumber: string });

    const {
        scrollDivRef,
        bankDetails,
        bankDetailErrors,
        daysOptions,
        onChange,
        onSortCodeChange,
        onPaymentDayChange,
        getErrors,
        onSubmit,
        onYesButtonClick
    } = useBankDetailsForm({
        onSuccess: () => {
            setValidated(true);
            toast.success(t('repayment_setup.validate-bank-success'), {
                onClose: () => {
                    navigate(RoutePaths.ViewRequests);
                }
            });
        },
        t,
        showPaymentDay: false,
        accountType: AccountType.Disbursement,
        referenceNumber: policyReferenceNumber?.referenceNumber,
        businessPartnerId: bankDetailsState.businessPartnerId
    });

    useEffect(() => {
        loadRepaymentBankDetails();
    }, [policyReferenceNumber]);

    const loadRepaymentBankDetails = async () => {
        const successCallback = (result: BankDetailType) => {
            if (result.accountNumber && result.accountNumber !== '') {
                setBankDetailsState({
                    accountHolderName: result.accountHolderName || '',
                    accountNumber: result.accountNumber || '',
                    sortCode: (result.sortCode
                        ? result.sortCode.split('-')
                        : ['', '', '']
                    ).map((part) => ({ key: part, value: part })),
                    paymentDay: '',
                    businessPartnerId: result.businessPartnerId
                });
                setShowBankInputSection(false);
            } else {
                setShowBankInputSection(true);
            }
        };

        const failureCallback = () => {
            console.log('Failed to load direct debit details');
        };

        const token = await getAccessTokenSilently();

        new PaymentApi().GetDirectDebit(
            policyReferenceNumber?.referenceNumber,
            token,
            successCallback,
            failureCallback,
            new AbortController().signal
        );
    };

    const onBack = () => {
        navigate(RoutePaths.ViewRequests);
    };
    const onNoButtonClick = () => {
        setShowBankInputSection(true);
    };

    return (
        <div ref={scrollDivRef}>
            <BankDetailsCommon
                title={t('repayment_bank_details_title')}
                instruction={t('repayment_bank_details_instruction')}
                existingBankDetails={bankDetailsState}
                bankDetails={bankDetails}
                bankDetailErrors={bankDetailErrors}
                daysOptions={daysOptions}
                t={t}
                onChange={onChange}
                onSortCodeChange={onSortCodeChange}
                onPaymentDayChange={onPaymentDayChange}
                onSubmit={onSubmit}
                onYesButtonClick={onYesButtonClick}
                onNoButtonClick={onNoButtonClick}
                onBack={onBack}
                getErrors={getErrors}
                showPaymentDay={false}
                accountType={AccountType.Disbursement}
                showBankInputSection={showBankInputSection}
            />
        </div>
    );
};

export default RepaymentSetupPage;
